var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import useResizeObserver from 'use-resize-observer';
const ContainerBox = styled(Box) `
    width: 100%;
    height: 100%;
    overflow: auto;
    position: relative;
`;
export const ResizableContent = (props) => {
    const { children, view = 'fit-container', contentPosition = 'center' } = props, other = __rest(props, ["children", "view", "contentPosition"]);
    const ref = useRef(null);
    const [contentSize, setContentSize] = useState({ width: 1, height: 1 });
    const { width: wrapperWidth = 1, height: wrapperHeight = 1 } = useResizeObserver({ ref });
    const isDown = useRef(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const scrollLeft = useRef(0);
    const scrollTop = useRef(0);
    const getChildElement = () => {
        const containerElement = ref.current;
        return containerElement === null || containerElement === void 0 ? void 0 : containerElement.firstChild;
    };
    const updateStyles = useCallback(({ width, height }) => {
        const element = ref.current;
        const childElement = getChildElement();
        if (!element || !childElement) {
            return;
        }
        // Remember old values before actual content resize
        const oldScrollableWidth = element.scrollWidth - element.clientWidth;
        const oldLeft = element.scrollLeft;
        const leftRatio = oldLeft / oldScrollableWidth;
        const oldScrollableHeight = element.scrollHeight - element.clientHeight;
        const oldTop = element.scrollTop;
        const topRatio = oldTop / oldScrollableHeight;
        // Base required styles
        childElement.style.position = 'absolute';
        childElement.style.display = 'block';
        childElement.style.overflow = 'hidden';
        // Remove styles that could break this functionality
        childElement.style.maxWidth = 'none';
        childElement.style.maxHeight = 'none';
        // Resize content
        childElement.style.width = `${width}px`;
        childElement.style.height = `${height}px`;
        // Position content
        if (contentPosition === 'center') {
            childElement.style.left = `max((100% - ${width}px) / 2, 0px)`;
            childElement.style.top = `max((100% - ${height}px) / 2, 0px)`;
        }
        else if (contentPosition === 'top-left') {
            childElement.style.left = '0px';
            childElement.style.top = '0px';
        }
        else if (contentPosition === 'top-right') {
            childElement.style.right = '0px';
            childElement.style.top = '0px';
        }
        else if (contentPosition === 'bottom-left') {
            childElement.style.left = '0px';
            childElement.style.top = `max(100% - ${height}px, 0px)`;
        }
        else if (contentPosition === 'bottom-right') {
            childElement.style.left = `max(100% - ${width}px, 0px)`;
            childElement.style.right = '0px';
        }
        else if (contentPosition === 'top-center') {
            childElement.style.left = `max((100% - ${width}px) / 2, 0px)`;
            childElement.style.top = '0px';
        }
        else if (contentPosition === 'bottom-center') {
            childElement.style.left = `max((100% - ${width}px) / 2, 0px)`;
            childElement.style.bottom = '0px';
        }
        // Calculate new scroll positions
        const newScrollableWidth = element.scrollWidth - element.clientWidth;
        const newScrollLeft = newScrollableWidth * leftRatio;
        const newScrollableHeight = element.scrollHeight - element.clientHeight;
        const newScrollTop = newScrollableHeight * topRatio;
        if (newScrollableWidth > 0 || newScrollableHeight > 0) {
            childElement.style.cursor = 'move';
        }
        else {
            childElement.style.cursor = 'default';
        }
        element.scrollLeft = newScrollLeft;
        element.scrollTop = newScrollTop;
    }, [contentPosition]);
    useEffect(() => {
        if (typeof view === 'number') {
            updateStyles({
                width: Math.round(contentSize.width * view),
                height: Math.round(contentSize.height * view)
            });
        }
        if (view === 'fit-container') {
            // scale wrapper to fit container
            let newSizeWidth;
            let newSizeHeight;
            const contentRatio = contentSize.width / contentSize.height;
            const wrapperRatio = wrapperWidth / wrapperHeight;
            if (wrapperRatio > contentRatio) {
                // wrapper is wider than video
                newSizeWidth = wrapperHeight * contentRatio;
                newSizeHeight = wrapperHeight;
            }
            else {
                newSizeWidth = wrapperWidth;
                newSizeHeight = wrapperWidth / contentRatio;
            }
            updateStyles({
                width: Math.round(newSizeWidth),
                height: Math.round(newSizeHeight)
            });
        }
    }, [contentSize, wrapperWidth, wrapperHeight, view, updateStyles]);
    useEffect(() => {
        const childElement = getChildElement();
        if (!childElement) {
            return;
        }
        const allowedTags = ['IMG', 'VIDEO'];
        if (!allowedTags.includes(childElement.tagName)) {
            throw new Error(`Unsupported child element. Use one of the following tags: ${allowedTags.join(', ')}`);
        }
        const calculateSourceSize = () => {
            let size;
            if (childElement.tagName === 'IMG') {
                size = {
                    width: childElement.naturalWidth,
                    height: childElement.naturalHeight
                };
            }
            else if (childElement.tagName === 'VIDEO') {
                size = {
                    width: childElement.videoWidth,
                    height: childElement.videoHeight
                };
            }
            else {
                return;
            }
            setContentSize(size);
            updateStyles(size);
        };
        calculateSourceSize();
        childElement.addEventListener('load', calculateSourceSize);
        childElement.addEventListener('resize', calculateSourceSize);
        return () => {
            childElement.removeEventListener('load', calculateSourceSize);
            childElement.removeEventListener('resize', calculateSourceSize);
        };
    }, [updateStyles]);
    useEffect(() => {
        // whenever view changes, reset the scroll position
        const element = ref.current;
        if (!element) {
            return;
        }
        element.scrollLeft = 0;
        element.scrollTop = 0;
        scrollLeft.current = 0;
        scrollTop.current = 0;
    }, [view]);
    useEffect(() => {
        const childElement = getChildElement();
        if (!childElement) {
            return;
        }
        const getCoords = (event) => {
            const clientX = 'clientX' in event ? event.clientX : event.touches[0].clientX;
            const clientY = 'clientY' in event ? event.clientY : event.touches[0].clientY;
            return { x: clientX, y: clientY };
        };
        const onMouseDown = (event) => {
            isDown.current = true;
            const element = ref.current;
            if (!element) {
                return;
            }
            const { x, y } = getCoords(event);
            startX.current = x - element.offsetLeft;
            startY.current = y - element.offsetTop;
            scrollLeft.current = element.scrollLeft || 0;
            scrollTop.current = element.scrollTop || 0;
            event.preventDefault();
        };
        const onMouseUp = () => {
            isDown.current = false;
        };
        const onMouseMove = (event) => {
            const element = ref.current;
            if (!isDown.current || !element) {
                return;
            }
            const coords = getCoords(event);
            const x = coords.x - element.offsetLeft;
            const y = coords.y - element.offsetTop;
            const walkX = x - startX.current;
            const walkY = y - startY.current;
            element.scrollLeft = scrollLeft.current - walkX;
            element.scrollTop = scrollTop.current - walkY;
        };
        const onDragStart = (event) => {
            event.preventDefault();
        };
        childElement.addEventListener('mousedown', onMouseDown);
        childElement.addEventListener('dragstart', onDragStart);
        childElement.addEventListener('touchstart', onMouseDown);
        // These are better off on document to support dragging outside of the element
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onMouseMove);
        document.addEventListener('touchend', onMouseUp);
        return () => {
            childElement.removeEventListener('mousedown', onMouseDown);
            childElement.removeEventListener('dragstart', onDragStart);
            childElement.removeEventListener('touchstart', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onMouseMove);
            document.removeEventListener('touchend', onMouseUp);
        };
    }, []);
    return (_jsx(ContainerBox, Object.assign({ ref: ref }, other, { children: children })));
};
